import { useState } from "react";
import { Award, TrendingUp, Hourglass, ShoppingBasket } from "lucide-react";

const GOLD_GRADIENT = "linear-gradient(135deg, #cfa64a 0%, #f4e2a4 45%, #e0bf55 70%, #cfa64a 100%)";

// Each badge gets its own coordinated colour (solid tint + white text/icon).
const CONFIG = {
  bestseller: { label: "Bestseller", icon: Award, gradient: GOLD_GRADIENT, textDark: true },
  popular: { label: "Popular", icon: TrendingUp, bg: "#6366f1" },   // indigo
  almost_gone: { label: "Almost Gone", icon: Hourglass, bg: "#082F24" }, // Zebaa green, sits with the gold and the Sale tag
};

const BASKET_COLOR = "#111111"; // black

// Live, "alive" detail shown when a badge is tapped/hovered.
function badgeDetail(key, product) {
  const v = product?.views_24h || 0;
  const sold = product?.sold_count || 0;
  switch (key) {
    case "bestseller":
      return sold > 0 ? `${sold} sold — a customer favourite` : "One of our best sellers";
    case "popular":
      return v > 0 ? `${v} views in the last 24h` : "Popular with shoppers";
    case "almost_gone":
      return "Selling fast — only a few left";
    case "new":
      return "Just arrived — fresh in this week";
    default:
      return "";
  }
}

export const ProductBadges = ({ product, className = "", exclude = [] }) => {
  const badges = (Array.isArray(product?.badges) ? product.badges : []).filter((b) => !exclude.includes(b));
  const basket = exclude.includes("baskets") ? 0 : (product?.basket_count || 0);
  const [active, setActive] = useState(null);
  if (!badges.length && basket <= 0) return null;

  const Chip = ({ id, icon: Icon, bg, gradient, textDark, label, detail }) => {
    const open = active === id;
    const txt = textDark ? "text-black" : "text-white";
    const ring = textDark ? "ring-black/5" : "ring-black/10";
    return (
      <span
        className="relative inline-flex"
        onMouseEnter={() => setActive(id)}
        onMouseLeave={() => setActive((a) => (a === id ? null : a))}
      >
        <button
          type="button"
          data-testid={`badge-${id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActive(open ? null : id);
          }}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full shadow-sm ring-1 ${ring} ${txt} cursor-pointer hover:brightness-105 active:scale-95 transition-all`}
          style={gradient ? { backgroundImage: gradient } : { backgroundColor: bg }}
        >
          {Icon && <Icon className={`w-3 h-3 ${txt}`} strokeWidth={2.6} />}
          {label}
        </button>
        {open && detail && (
          <span
            data-testid={`badge-tip-${id}`}
            className="absolute left-0 top-full mt-1.5 z-30 whitespace-nowrap rounded-md bg-charcoal text-white text-[10px] normal-case tracking-normal font-medium px-2.5 py-1.5 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
          >
            {detail}
          </span>
        )}
      </span>
    );
  };

  const rank = product?.bestseller_rank || 0;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="product-badges">
      {badges.map((b) => {
        const c = CONFIG[b];
        if (!c) return null;
        const label = b === "bestseller" && rank > 0 ? `#${rank} Bestseller` : c.label;
        return <Chip key={b} id={b} icon={c.icon} bg={c.bg} gradient={c.gradient} textDark={c.textDark} label={label} detail={badgeDetail(b, product)} />;
      })}
      {basket > 0 && (
        <Chip
          id="baskets"
          icon={ShoppingBasket}
          bg={BASKET_COLOR}
          label={`In ${basket} ${basket === 1 ? "basket" : "baskets"}`}
          detail={`In ${basket} shopping ${basket === 1 ? "bag" : "bags"} right now`}
        />
      )}
    </div>
  );
};

export const PRODUCT_BADGE_OPTIONS = [
  { value: "bestseller", label: "Bestseller" },
  { value: "popular", label: "Popular" },
  { value: "almost_gone", label: "Almost Gone" },
];
